import { Checkbox, InputField, SelectSimple } from '@advanza/input'
import { Divider, Icon, PreIcon } from '@advanza/ui'
import MonthPicker from 'components/billing/MonthPicker'
import DatePicker from 'components/DatePicker'
import AutoRow from 'components/misc/AutoRow'
import Pagination from 'components/Pagination'
import ServicesSelect from 'components/services/ServicesSelect'
import TableList from 'components/TableList'
import LoadBoxWrapper from 'components/ui/LoadBoxWrapper'
import { format } from 'date-fns'
import { useLostInvoicesList } from 'hooks/billingHooks'
import { useAppSelector } from 'hooks/hooks'
import { useDebouncedAmountFilter, useUrlQuery } from 'hooks/miscHooks'
import queryString from 'query-string'
import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

const definitions = {
    invoice_id: { name: 'ID' },
    invoice_nr: { name: 'Invoice nr' },
    marked_lost: { name: 'Datum' },
    service_provider_id: { name: 'Prov_id' },
    businessName: { name: 'Prov name' },
    amount_netto: { name: '€netto' },
    is_unsubscribed: { name: 'Unsubscribed' },
    lost_trigger: { name: 'Trigger' },
    debt_collection: { name: 'Debt coll.' },
}

const triggerLabels = {
    manual_bulk: 'Manual (bulk)',
    manual_bad_debt: 'Manual (bad debt)',
    payt: 'PAYT',
    auto_tiny: 'Auto (tiny invoice)',
}

const amountOptions = [
    { value: '<0', name: '< 0' },
    { value: '>0', name: '> 0' },
    { value: '<100', name: '< 100' },
    { value: '100 - 500', name: '100 - 500' },
    { value: '500 - 1000', name: '500 - 1000' },
    { value: '>1000', name: '> 1000' },
]

const triggerOptions = [
    { value: 'manual_bulk', name: triggerLabels.manual_bulk },
    { value: 'manual_bad_debt', name: triggerLabels.manual_bad_debt },
    { value: 'payt', name: triggerLabels.payt },
    { value: 'auto_tiny', name: triggerLabels.auto_tiny },
    { value: 'unknown', name: 'Unknown (historical)' },
]

function renderRow(id, entities) {
    const { invoices } = entities
    const invoice = invoices[id]

    return {
        cols: Object.keys(definitions).map((key) => {
            switch (key) {
                case 'marked_lost':
                    return invoice.marked_lost
                        ? format(new Date(invoice.marked_lost), 'd MMM yyyy HH:mm')
                        : ''
                case 'service_provider_id':
                    return invoice.service_provider_id
                case 'businessName':
                    return (
                        <Link
                            title={invoice.businessName}
                            to={`/service/providers/provider/${invoice.service_provider_id}/company`}>
                            {invoice.businessName}
                        </Link>
                    )
                case 'amount_netto':
                    return '€' + Number(invoice.amount_netto).toFixed(2)
                case 'is_unsubscribed':
                    return invoice.is_unsubscribed ? (
                        <span title={invoice.unsubscribed_at || ''}>
                            <Icon name="check" style={{ color: '#0ea66b' }} />
                            <span>&nbsp;yes</span>
                        </span>
                    ) : (
                        <span>no</span>
                    )
                case 'lost_trigger':
                    return triggerLabels[invoice.lost_trigger] || 'unknown'
                case 'debt_collection':
                    return invoice.debt_collection
                        ? format(new Date(invoice.debt_collection), 'd MMM yyyy')
                        : ''
                default:
                    return invoice[key]
            }
        }),
    }
}

const LostInvoicesList = ({ filterId = 'lost', defaultOptions = {} }) => {
    const { countries } = useAppSelector((state) => state.countries)
    const { protocol, hostname } = window.location
    const { query: urlQuery, onSetQuery } = useUrlQuery({
        ...defaultOptions,
    })
    const {
        filter = {},
        entities,
        currentPage,
        pageNr,
        ...rest
    } = useLostInvoicesList(filterId, { ...urlQuery, countries })

    const changeFilter = (changes) => {
        onSetQuery({ ...changes, selection: [] })
    }
    const { minAmount, setMinAmount, maxAmount, setMaxAmount } = useDebouncedAmountFilter(
        urlQuery,
        filter,
        changeFilter,
    )

    const [searchInput, setSearchInput] = useState('')

    useEffect(() => {
        rest.changeFilter({ ...urlQuery, selection: [] })
        setSearchInput(urlQuery.query || '')
    }, [urlQuery])

    const rows = {}
    currentPage.forEach((id) => {
        rows[id] = renderRow(id, entities)
    })

    // Build the export URL with the currently active filters.
    const exportFilter = {}
    Object.keys(filter).forEach((key) => {
        if (key === 'selection' || key === 'page' || key === 'pageSize' || key === 'searchKey') {
            return
        }
        const value = filter[key]
        if (value === undefined || value === null || value === '') {
            return
        }
        if (Array.isArray(value)) {
            exportFilter[key + '[]'] = value
        } else {
            exportFilter[key] = value
        }
    })
    const exportHref = `${protocol}//${hostname}/api/office/invoices/export-lost-list?${queryString.stringify(exportFilter)}`

    return (
        <LoadBoxWrapper isLoading={rest.isFetching}>
            <Divider />
            <AutoRow>
                <DatePicker
                    onChange={(e) => changeFilter({ dateFrom: e.target.value || '' })}
                    format="YYYY-MM-DD"
                    value={filter.dateFrom || ''}
                    placeholder="From"
                />
                <DatePicker
                    onChange={(e) => changeFilter({ dateTo: e.target.value || '' })}
                    format="YYYY-MM-DD"
                    value={filter.dateTo || ''}
                    placeholder="To"
                />
                <MonthPicker
                    value={filter.aboutMonth}
                    onChange={(aboutMonth) => changeFilter({ aboutMonth: aboutMonth ?? '' })}
                    startYearMonth="2019-10"
                />
                <InputField
                    icon="search"
                    placeholder="Enter query and press enter"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            changeFilter({ query: searchInput })
                        }
                    }}
                />
                <SelectSimple
                    options={amountOptions}
                    onChange={(e) => {
                        setMinAmount('')
                        setMaxAmount('')
                        changeFilter({
                            invoiceAmount: e.target.value,
                            minInvoiceAmount: '',
                            maxInvoiceAmount: '',
                        })
                    }}
                    value={filter.invoiceAmount}
                    emptyOption
                    alwaysControlValue
                    placeholder="min invoice amount"
                />
                <InputField
                    type="number"
                    step="0.01"
                    placeholder="min €netto"
                    value={minAmount}
                    onChange={(e) => setMinAmount(e.target.value)}
                />
                <InputField
                    type="number"
                    step="0.01"
                    placeholder="max €netto"
                    value={maxAmount}
                    onChange={(e) => setMaxAmount(e.target.value)}
                />
                <ServicesSelect
                    onChangeValue={(serviceIds) => changeFilter({ serviceIds })}
                    value={filter.serviceIds || []}
                    placeholder="Select service"
                    emptyOption
                />
                <SelectSimple
                    options={triggerOptions}
                    onChange={(e) => changeFilter({ lostTrigger: e.target.value })}
                    value={filter.lostTrigger}
                    emptyOption
                    alwaysControlValue
                    placeholder="Trigger"
                />
                <Checkbox
                    value={Number(filter.includeDebtCollection) === 1}
                    onChange={() => {
                        const next = Number(filter.includeDebtCollection) === 1 ? '0' : '1'
                        onSetQuery({ includeDebtCollection: next })
                        rest.changeFilter(
                            { includeDebtCollection: next, selection: [], page: 0 },
                            true,
                        )
                    }}>
                    Include debt collection
                </Checkbox>
                {rest.count !== undefined && (
                    <Pagination
                        current={pageNr}
                        pageSize={parseInt(filter.pageSize) || 20}
                        count={parseInt(rest.count) || 0}
                        onPageChange={(page) => changeFilter({ page })}
                    />
                )}
                <a target="_blank" rel="noopener noreferrer" href={exportHref}>
                    <PreIcon name="exit_to_app">Export CSV</PreIcon>
                </a>
            </AutoRow>
            <Divider m />
            <TableList
                {...rest}
                selectable={false}
                changeFilter={rest.changeFilter || (() => {})}
                filter={filter}
                rows={rows}
                definitions={definitions}
            />
        </LoadBoxWrapper>
    )
}

export default LostInvoicesList
